import { User } from '@sr/types';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Price as PriceType } from './prices';
import redirectToCheckout from './redirect-to-checkout';
import { AppearFadeInTransition, getCardTransitionTime } from './transitions';
import { Heading, Subtext, Toggle } from './ui';
import { Sentry } from 'utils/sentry';
import { useIsMobile } from 'v1/hooks/use-is-media-query';
import usePreventColdStart from 'v1/hooks/use-prevent-cold-start';
import { useUser } from 'v1/hooks/use-user';
import { ArrowRightIcon, Box, Button } from 'v1/ui';
import { SpinnerIcon } from 'v1/ui/spinner/SpinnerIcon';
import API from 'v1/utils/api';

type Props = {
  defaultPriceId: string;
  heading: React.ReactNode;
  numberOfCards: number;
  prices: PriceType[];
  subtext: React.ReactNode;
};

export default function PricePicker({
  defaultPriceId,
  heading,
  numberOfCards,
  prices,
  subtext,
}: Props) {
  const isMobile = useIsMobile();
  const user = useUser();
  const [activeId, setActiveId] = useState(defaultPriceId);
  const [loading, setLoading] = useState(false);
  const transitionDelay = isMobile
    ? 0
    : getCardTransitionTime(numberOfCards) - 400;
  useLogViewEvent();
  usePreventColdStart();

  function startCheckout() {
    if (!user) {
      throw new Error('User required to start checkout');
    }
    if (!loading) {
      setLoading(true);
      redirectToCheckout(activeId, user).catch((error) => {
        Sentry.captureException(error, { tags: { noFeedback: true } });
        setLoading(false);
        alert(
          'Error checking out. Please try again or email hello@standardresume.co'
        );
      });
    }
  }

  return (
    <AppearFadeInTransition delay={transitionDelay}>
      <Box sx={{ mb: [24, 0] }}>
        <Heading>{heading}</Heading>
        <Subtext>{subtext}</Subtext>
        <Container>
          {prices.map((price) => (
            <Price
              active={activeId === price.id}
              onClick={() => setActiveId(price.id)}
              heading={price.heading}
              subheading={price.subheading}
              popular={price.isPopular}
              key={price.id}
            />
          ))}
          <ButtonContainer>
            <SubscribeButton
              text="Subscribe"
              onClick={startCheckout}
              loading={loading}
            />
          </ButtonContainer>
        </Container>
        <Box
          sx={{
            mt: 12,
            mb: 6,
            textAlign: 'center',
            color: 'black80',
            bg: 'blue10',
            p: 3,
            borderRadius: 16,
          }}
        >
          Can't afford a subscription? <br />
          You may qualify for{' '}
          <a
            href="https://standardresume.co/financial-assistance"
            target="_blank"
            rel="noreferrer"
            style={{ color: 'rgba(42, 63, 251, 1)', textDecoration: 'none' }}
          >
            financial assistance
          </a>
          .
        </Box>
        <BackButton />
      </Box>
    </AppearFadeInTransition>
  );
}

// Log view event to analytics
function useLogViewEvent(user?: User) {
  useEffect(() => {
    if (user) {
      API.events.logEvent({
        userId: user.id,
        eventName: 'Product Viewed',
        properties: {
          variant: 'Combined',
        },
      });
    }
  }, [user]);
}

type PriceProps = {
  active: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  popular?: boolean;
  subheading?: string;
  heading: string;
};

function Price({ active, heading, onClick, subheading, popular }: PriceProps) {
  return (
    <Button
      onClick={onClick}
      variant="noStyles"
      sx={{
        display: 'flex',
        width: '100%',
        minHeight: 70,
        alignItems: 'center',
        p: 3,
        bg: active ? 'white' : 'transparent',
        borderRadius: 8,
        color: 'black',
        lineHeight: 1.2,
      }}
    >
      <Toggle active={active} />
      <Box sx={{ pl: 2, flexGrow: 1 }}>
        <Box sx={{ fontSize: 16, fontWeight: 600 }}>{heading}</Box>
        {subheading && (
          <Box sx={{ pt: 1, fontSize: 12, fontWeight: 500, color: 'black60' }}>
            {subheading}
          </Box>
        )}
      </Box>
      {popular && (
        <Box
          sx={{
            color: 'rgba(232, 116, 116, 1)',
            fontWeight: 600,
            fontSize: 12,
            textAlign: 'center',
          }}
        >
          Most <br />
          Popular!
        </Box>
      )}
    </Button>
  );
}

const sharedButtonStyles = {
  minHeight: 56,
  display: 'flex',
  borderRadius: 8,
  px: 4,
  py: 3,
};

function MoneyBackGuarantee({
  line1,
  line2,
}: {
  line1: string;
  line2: string;
}) {
  return (
    <Box
      sx={{
        ...sharedButtonStyles,
        // Change order so this shows below subscribe when buttons stacked on mobile
        order: [1, 0],
        flexDirection: 'column',
        justifyContent: 'center',
        border: '1px solid black',
        borderColor: 'black10',
        px: 3,
        textAlign: ['center', 'left'],
      }}
    >
      <Box sx={{ fontSize: [14, 12], fontWeight: 600, color: 'black70' }}>
        {line1}
      </Box>
      <Box sx={{ fontSize: [12, 10], fontWeight: 600, color: 'black70' }}>
        {line2}
      </Box>
    </Box>
  );
}

type SubscribeProps = {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  loading: boolean;
  text: string;
};
function SubscribeButton({ onClick, loading, text }: SubscribeProps) {
  return (
    <Button
      onClick={onClick}
      data-testid="start-checkout"
      sx={{
        ...sharedButtonStyles,
        justifyContent: 'space-between',
        bg: loading ? 'blue60' : 'blue',
        transition: 'background 300ms linear',
        outline: 'none',
        ':hover': {
          bg: loading ? 'blue60' : 'blue90',
          outline: 'none',
        },
        ':focus': { outline: 'none' },
      }}
    >
      {text}
      {loading ? <SpinnerIcon /> : <ArrowRightIcon color="white" />}
    </Button>
  );
}

function Container({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bg: 'buttonGray',
        p: 3,
        borderRadius: 16,
      }}
    >
      {children}
    </Box>
  );
}

function ButtonContainer({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        // display: 'grid',
        // gridTemplateColumns: ['1fr', '1fr 1fr'],
        // gridGap: 2,
        py: 5,
      }}
    >
      {children}
    </Box>
  );
}

function BackButton() {
  const history = useHistory();

  function handleClick() {
    // @ts-ignore: it's confused and is using rr-v6 types
    if (history.length > 0) {
      // @ts-ignore: it's confused and is using rr-v6 types
      history.goBack();
    } else {
      history.push('/resumes');
    }
  }

  return (
    <Button
      variant="noStyles"
      onClick={handleClick}
      sx={{
        display: 'block',
        width: '100%',
        mt: 4,
        color: 'black50',
        textAlign: 'center',
        ':hover': {
          textDecoration: 'underline',
        },
      }}
    >
      No thanks, I'll stick with the free plan
    </Button>
  );
}
